import { createReducer, createAction } from "@reduxjs/toolkit";
import { call, put, takeLatest } from "redux-saga/effects";

import { fetchTrajectoryParameters, htpmdVersion } from "../lib/api";

export const actions = {
  request_trajectory_search_parameters: createAction("REQUEST_TRAJECTORY_SEARCH_PARAMETERS"),
  receive_trajectory_search_parameters: createAction("RECEIVE_TRAJECTORY_SEARCH_PARAMETERS"),
  receive_trajectory_aggregate_data: createAction("RECEIVE_TRAJECTORY_AGGREGATE_DATA"),
  receive_trajectory_search_parameters_failed: createAction("RECEIVE_TRAJECTORY_SEARCH_PARAMETERS_FAILED"),
  toggle_enumerable: createAction("TOGGLE_TRAJECTORY_ENUMERABLE"),
  toggle_property: createAction("TOGGLE_TRAJECTORY_PROPERTY"),
  update_range: createAction("UPDATE_TRAJECTORY_RANGE"),
  toggle_range_filter: createAction("TOGGLE_TRAJECTORY_RANGE_FILTER"),
};

export const parameters = {
  //component fields
  ANION_RAW_TYPE: "anion_raw_type",
  CATION_RAW_TYPE: "cation_raw_type",
  MATERIAL_GROUP: "material_group",
  //numeric range fields
  MOLALITY: "molality",
  TEMPERATURE: "temperature",
  //condition fields
  FORCE_FIELD: "force_field",
  MONOMER_MOLECULAR_WEIGHT: "monomer_molecular_weight",
  TIME_STEP: "time_step",
  SIMULATION_LENGTH: "simulation_length",
  DEGREE_OF_POLYMERIZATION: "degree_of_polymerization",
  DENSITY: "density",
  //property fields
  CONDUCTIVITY: "conductivity",
  LI_DIFFUSIVITY: "li_diffusivity",
  TFSI_DIFFUSIVITY: "tfsi_diffusivity",
  POLY_DIFFUSIVITY: "poly_diffusivity",
  TRANSFERENCE_NUMBER: "transference_number",
  CONTAINS_STRUCTURE: "contains_structure",
  CONTAINS_LI_MSD_CURVE: "contains_li_msd_curve",
  CONTAINS_TFSI_MSD_CURVE: "contains_tfsi_msd_curve",
};

export const constants = {
  //loading states
  LOADED: "LOADED",
  LOADING: "LOADING",
  ERROR: "ERROR",
  ...parameters,
};

export const components = [constants.ANION_RAW_TYPE, constants.CATION_RAW_TYPE, constants.MATERIAL_GROUP];

export const conditions = [
  constants.FORCE_FIELD,
  constants.TIME_STEP,
  constants.TEMPERATURE,
  constants.SIMULATION_LENGTH,
];

export const enumerables = components.concat(conditions);

export const properties = [
  { label: constants.CONDUCTIVITY, max: 0.00669341842301065, min: 0.0000024822221924335, step: 0.0000024822221924335 },
  { label: constants.LI_DIFFUSIVITY, max: 0.00000222532545986001, min: 1.44598896469739e-9, step: 1.44598896469739e-9 },
  {
    label: constants.TFSI_DIFFUSIVITY,
    max: 0.0000024483930091893,
    min: 2.35711224761875e-9,
    step: 2.35711224761875e-9,
  },
  {
    label: constants.POLY_DIFFUSIVITY,
    max: 0.00000260720246753981,
    min: 9.03742740319782e-10,
    step: 9.03742740319782e-10,
  },
  { label: constants.TRANSFERENCE_NUMBER, max: 0.594548103497676, min: -1.64186423613927, step: 0.004472824679274 },
  { label: constants.DENSITY, max: 0.594548103497676, min: -1.64186423613927, step: 0.004472824679274 },
];

export const numericRangeFields = properties.concat([
  { label: constants.MOLALITY, max: 9, min: 0 },
  { label: constants.MONOMER_MOLECULAR_WEIGHT, max: 400, min: 0 },
  { label: constants.DEGREE_OF_POLYMERIZATION, max: 400, min: 0 },
]);

export const availableData = [
  constants.CONTAINS_STRUCTURE,
  constants.CONTAINS_LI_MSD_CURVE,
  constants.CONTAINS_TFSI_MSD_CURVE,
];

export const initState = {
  loadingState: constants.LOADING,
  ...enumerables.reduce((acc, enumerable) => {
    acc[enumerable] = {
      active: {},
      available: [],
      checked: [],
    };
    return acc;
  }, {}),
  ...numericRangeFields.reduce((acc, field) => {
    acc[field.label] = {
      active: true,
      range: [field.min, field.max],
      filterRange: [field.min, field.max],
    };
    return acc;
  }, {}),
  ...availableData.reduce((acc, property) => {
    acc[property] = { active: true };
    return acc;
  }, {}),
};

export const actionMap = {
  [actions.request_trajectory_search_parameters]: (state) => {
    state.loadingState = constants.LOADING;
    return state;
  },
  [actions.receive_trajectory_search_parameters]: (state, action) => {
    const { query_params } = action.payload;
    state.loadingState = constants.LOADED;

    enumerables.forEach((enumerable) => {
      const uniqueParams = [...new Set(query_params[enumerable])];
      state[enumerable].available = uniqueParams;
      state[enumerable].checked = uniqueParams.reduce((acc, field) => {
        //default all enumerable fields as active in search
        acc[field] = true;
        return acc;
      }, {});
    });

    numericRangeFields.forEach((field) => {
      if (query_params[field.label]) {
        state[field.label].range = query_params[field.label];
        state[field.label].filterRange = query_params[field.label];
      }
    });

    state.loadingState = constants.LOADED;

    return state;
  },
  [actions.receive_trajectory_aggregate_data]: (state, action) => {
    const { trajectoryData } = action.payload;

    // set the max and min of each property based on the trajectory data
    properties.forEach((property) => {
      const propValues = trajectoryData.map((trajectory) => trajectory[property.label]);
      const min = Math.min(...propValues);
      const max = Math.max(...propValues);

      state[property.label].range = [min, max];
      state[property.label].filterRange = [min, max];
    });

    return state;
  },
  [actions.receive_trajectory_search_parameters_failed]: (state) => {
    return { ...state, loadingState: constants.ERROR };
  },
  [actions.toggle_enumerable]: (state, action) => {
    const { enumerable, field } = action.payload;
    state[enumerable].checked[field] = !state[enumerable].checked[field];
    return state;
  },
  [actions.toggle_property]: (state, action) => {
    const { property } = action.payload;
    state[property].checked = !state[property].checked;
    return state;
  },
  [actions.update_range]: (state, action) => {
    const { filterRange, field } = action.payload;
    state[field].filterRange = filterRange;
    return state;
  },
  [actions.toggle_range_filter]: (state, action) => {
    const { field } = action.payload;
    state[field].checked = !state[field].checked;
    return state;
  },
};

export const reducer = createReducer(initState, actionMap);

const getMDSearch = (state) => state.mdSearch;
export const selectors = {
  getMDSearch,
};

function* onFetchTrajectorySearchParameters(action) {
  try {
    const { accessToken } = action.payload;
    const { data: query_params } = yield call(fetchTrajectoryParameters, {
      htpmd_version: htpmdVersion,
      accessToken,
    });
    yield put(actions.receive_trajectory_search_parameters({ query_params }));
  } catch (e) {
    console.warn(e);
    yield put(actions.receive_trajectory_search_parameters_failed());
  }
}

export const sagas = {
  watchEveryRequestTrajectorySearchParameters: function* watchEveryRequestTrajectorySearchParameters() {
    yield takeLatest(actions.request_trajectory_search_parameters.toString(), onFetchTrajectorySearchParameters);
  },
};
